import React, { useMemo, useState } from "react";
import { ClipboardList, Crosshair, Power, X, Search } from "lucide-react";
import { useGame } from "../game/em2c-store";
import { EM2C_BUILDINGS, EM2C_CATEGORIES } from "../game/data/em2c-buildings";
import { getBuildingPriority } from "../game/systems/ColonyResourceController";

// Permanent build-number order is preserved in the save. Older saves are
// assigned their original array order on load.
export default function BuildingDirectory() {
  const [open, setOpen] = useState(false);
  React.useEffect(() => {
    const close = (e) => { if (e.detail?.name !== "directory") setOpen(false); };
    window.addEventListener("mars:menu-open", close);
    const toggle = () => { window.dispatchEvent(new CustomEvent("mars:menu-open", { detail: { name: "directory" } })); setOpen(v => !v); };
    window.addEventListener("mars:toggle-directory", toggle);
    window.addEventListener("mars:world-click", close);
    return () => { window.removeEventListener("mars:menu-open", close); window.removeEventListener("mars:toggle-directory", toggle); window.removeEventListener("mars:world-click", close); };
  }, []);
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const buildings = useGame(s => s.buildings);
  const selectedId = useGame(s => s.selectedId);
  const selectBuilding = useGame(s => s.selectBuilding);
  const toggleActive = useGame(s => s.toggleActive);
  const setBuildingAuto = useGame(s => s.setBuildingAuto);
  const setBuildingPriority = useGame(s => s.setBuildingPriority);
  const overrides = useGame(s => s.buildingOverrides || {});
  const autoIdle = useGame(s => s._autoIdleIds || []);

  const rows = useMemo(() => buildings.map((b, index) => ({
    ...b,
    buildNumber: b.buildNumber || index + 1,
    def: EM2C_BUILDINGS[b.type],
  })).filter(b => b.def)
    .filter(b => category === "all" || b.def.category === category)
    .filter(b => `${b.def.name} ${b.buildNumber} ${b.state || ""}`.toLowerCase().includes(query.toLowerCase()))
    .sort((a, b) => a.buildNumber - b.buildNumber), [buildings, category, query]);

  const locate = (b) => {
    selectBuilding(b.id);
    window.dispatchEvent(new CustomEvent("mars:focus-building", {
      detail: { x: b.x, z: b.z, q: b.q, r: b.r },
    }));
  };

  const status = (b) => {
    if (b.progress < 1) return "Constructing";
    if (b.active === false) return "Manual off";
    if (autoIdle.includes(b.id)) return "Auto standby";
    return (b.controllerReason || b.state || "Operational").replaceAll("_", " ");
  };
  const statusColor = (b) => b.active === false ? "#f59e0b"
    : autoIdle.includes(b.id) ? "#38bdf8"
    : b.state === "operational" ? "#00fa9a" : "#fbbf24";

  return (
    <div className="absolute top-16 left-14 z-[95] pointer-events-auto" style={{ fontFamily: "Rajdhani, sans-serif" }} data-testid="building-directory">
      {open && <><button aria-label="Close building directory overlay" onClick={() => setOpen(false)} className="fixed inset-0 z-[-1] cursor-default bg-black/35" /><div className="mt-2 w-[370px] max-w-[calc(100vw-1.5rem)] max-h-[calc(100vh-6rem)] flex flex-col bg-[#090d13] border border-cyan-400/30 shadow-2xl" data-testid="building-directory-panel">
        <div className="p-3 border-b border-white/10 flex gap-2">
          <select aria-label="Building category" value={category} onChange={e => setCategory(e.target.value)}
            className="min-w-0 flex-1 bg-[#151b25] border border-white/20 text-slate-100 p-2 text-xs">
            <option value="all">All categories</option>
            {EM2C_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <button onClick={() => setOpen(false)} aria-label="Close building directory" className="text-slate-400"><X size={16} /></button>
        </div>
        <label className="px-3 pt-2 flex items-center gap-2 text-slate-400">
          <Search size={13} />
          <input aria-label="Search buildings" value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search name, number, status" className="flex-1 min-w-0 p-1.5 bg-transparent border-b border-white/15 text-xs text-white outline-none" />
        </label>
        <div className="px-3 py-1 text-[10px] text-slate-500 font-mono">{rows.length} shown · numbered in construction order · blue = auto standby</div>
        <div className="overflow-y-auto flex-1 p-2 space-y-2" style={{ scrollbarWidth: "thin" }}>
          {rows.map(b => <div key={b.id} className={`border p-2 ${selectedId === b.id ? "border-cyan-400 bg-cyan-400/10" : "border-white/10 bg-[#151b25]"}`}>
            <button onClick={() => locate(b)} className="w-full flex items-start justify-between text-left gap-2" title="Locate and highlight on map">
              <div className="min-w-0">
                <div className="text-xs text-slate-100 font-bold truncate">#{b.buildNumber} {b.def.name}</div>
                <div className="text-[10px] font-mono capitalize" style={{ color: statusColor(b) }}>{status(b)}</div>
              </div>
              <Crosshair size={15} className="text-cyan-400 shrink-0" />
            </button>
            <div className="mt-2 flex items-center gap-1.5">
              <button onClick={() => toggleActive(b.id)} className={`px-2 py-1 text-[10px] border ${b.active !== false ? "border-green-400/40 text-green-300" : "border-amber-400/40 text-amber-300"}`} title="Manually enable or disable this building">
                <Power size={11} className="inline mr-1" />{b.active !== false ? "On" : "Off"}
              </button>
              <button onClick={() => setBuildingAuto(b.id, b.autoControl === false)}
                className={`px-2 py-1 text-[10px] border ${b.autoControl !== false ? "border-cyan-400/40 text-cyan-300" : "border-white/20 text-slate-400"}`} title="Allow the colony controller to put this building into standby">
                {b.autoControl !== false ? "Auto" : "Manual"}
              </button>
              <span className="ml-auto text-[10px] text-slate-500">Priority</span>
              <select aria-label={`Priority for ${b.def.name} #${b.buildNumber}`}
                value={overrides[b.id] ?? getBuildingPriority(b.type)} onChange={e => setBuildingPriority?.(b.id, Number(e.target.value))}
                className="bg-[#151b25] text-slate-200 border border-white/20 text-[10px] px-1 py-1">
                {[0,1,2,3,4,5].map(p => <option key={p} value={p}>P{p}</option>)}
              </select>
            </div>
          </div>)}
          {rows.length === 0 && <div className="text-xs text-slate-500 p-4">No matching buildings.</div>}
        </div>
      </div></>}
    </div>
  );
}
